import {
  Container,
  Graphics,
  RenderTexture,
  Sprite,
  Texture,
  type Renderer,
} from "pixi.js";
import type { GameConfig } from "../config/GameConfig";
import { clamp } from "../core/math";
import type { WorldBounds } from "../core/WorldBounds";
import type { Bubble } from "../entities/Bubble";
import type { Divider } from "../entities/Divider";
import type { ForceField } from "../physics/ForceField";
import { DividerBackdrop } from "./DividerBackdrop";
import { OrganicContourFilter } from "./OrganicContourFilter";

export class OrganicPipeline {
  readonly fieldLayer = new Container();
  readonly output = new Sprite();
  readonly debug = new Graphics();
  readonly debugVectors = new Sprite();
  readonly backdrop: DividerBackdrop;

  private readonly blobTexture: Texture;
  private readonly bubbleSprites: Sprite[] = [];
  private readonly lineSprites: Sprite[] = [];
  private readonly depositSprites: Sprite[] = [];

  private fieldTarget: RenderTexture;
  private readonly contour: OrganicContourFilter;

  private readonly forceCanvas: HTMLCanvasElement;
  private readonly forceCtx: CanvasRenderingContext2D;
  private forceImage: ImageData;
  private forceTexture: Texture;

  private readonly dividerCanvas: HTMLCanvasElement;
  private readonly dividerCtx: CanvasRenderingContext2D;
  private dividerImage: ImageData;
  private dividerTexture: Texture;

  private readonly debugCanvas: HTMLCanvasElement;
  private readonly debugCtx: CanvasRenderingContext2D;
  private debugTexture: Texture;

  constructor(
    private readonly config: GameConfig,
    private readonly bounds: WorldBounds,
    private readonly renderer: Renderer,
  ) {
    this.blobTexture = createSoftCircleTexture(256);
    this.contour = new OrganicContourFilter(config);
    this.backdrop = new DividerBackdrop(config, bounds);

    this.fieldTarget = RenderTexture.create({
      width: Math.max(1, bounds.width),
      height: Math.max(1, bounds.height),
      dynamic: true,
    });

    this.forceCanvas = document.createElement("canvas");
    this.forceCanvas.width = config.field.cols;
    this.forceCanvas.height = config.field.rows;
    this.forceCtx = this.forceCanvas.getContext("2d", { willReadFrequently: true })!;
    this.forceImage = this.forceCtx.createImageData(config.field.cols, config.field.rows);
    this.forceTexture = Texture.from(this.forceCanvas);
    this.forceTexture.source.scaleMode = "linear";
    this.forceTexture.source.addressMode = "clamp-to-edge";

    this.dividerCanvas = document.createElement("canvas");
    this.dividerCanvas.width = 1024;
    this.dividerCanvas.height = 1;
    this.dividerCtx = this.dividerCanvas.getContext("2d", { willReadFrequently: true })!;
    this.dividerImage = this.dividerCtx.createImageData(1024, 1);
    this.dividerTexture = Texture.from(this.dividerCanvas);
    this.dividerTexture.source.scaleMode = "linear";
    this.dividerTexture.source.addressMode = "clamp-to-edge";

    this.debugCanvas = document.createElement("canvas");
    this.debugCanvas.width = 1;
    this.debugCanvas.height = 1;
    this.debugCtx = this.debugCanvas.getContext("2d")!;
    this.debugTexture = Texture.from(this.debugCanvas);
    this.debugTexture.source.scaleMode = "nearest";
    this.debugVectors.texture = this.debugTexture;
    this.debugVectors.eventMode = "none";

    this.output.texture = this.fieldTarget;
    this.output.filters = [this.contour.filter];
    this.contour.attachMaps(this.forceTexture, this.dividerTexture);

    this.fieldLayer.sortableChildren = false;
    this.debug.eventMode = "none";
    this.output.eventMode = "none";
  }

  resize(width: number, height: number): void {
    const w = Math.max(1, Math.floor(width));
    const h = Math.max(1, Math.floor(height));
    this.fieldTarget.resize(w, h);
    this.output.texture = this.fieldTarget;
    this.output.width = w;
    this.output.height = h;
  }

  render(
    bubbles: readonly Bubble[],
    divider: Divider,
    field: ForceField,
    time: number,
  ): void {
    const w = this.bounds.width;
    const h = this.bounds.height;
    this.output.width = w;
    this.output.height = h;

    this.syncBlobs(bubbles, divider);
    this.updateForceMap(field);
    divider.writeDividerYMap(this.dividerImage);
    this.dividerCtx.putImageData(this.dividerImage, 0, 0);
    this.dividerTexture.source.update();
    this.backdrop.draw(divider);

    this.contour.sync(time, w, h, this.renderer.resolution, divider);

    this.renderer.render({
      container: this.fieldLayer,
      target: this.fieldTarget,
      clear: true,
      clearColor: [0, 0, 0, 1],
    });

    if (this.config.visual.debugForces) {
      this.drawDebug(field, divider, time);
    } else {
      this.debug.clear();
      this.debugVectors.visible = false;
    }
  }

  private syncBlobs(bubbles: readonly Bubble[], divider: Divider): void {
    const pad = this.config.visual.metaballPad;
    let i = 0;
    for (const bubble of bubbles) {
      const sprite = this.acquire(this.bubbleSprites, i);
      const meeting = bubble.state === "Meeting";
      const glow = meeting ? bubble.meetGlow : 0;
      const tint = meeting
        ? 0xff0000 | (Math.round(clamp(glow, 0, 1) * 255) << 8)
        : 0xff0000;
      this.placeBlob(sprite, bubble.x, bubble.y, bubble.radius * pad, tint);
      i++;
    }
    this.hideFrom(this.bubbleSprites, i);

    const lineRadius = this.config.divider.thickness * this.config.divider.sphereRadiusFactor;
    let li = 0;
    for (const p of divider.points) {
      const sprite = this.acquire(this.lineSprites, li);
      this.placeBlob(sprite, p.displayX, p.displayY, lineRadius * p.radiusScale, 0xff0000);
      li++;
    }
    this.hideFrom(this.lineSprites, li);

    let di = 0;
    for (const d of divider.deposits) {
      const sprite = this.acquire(this.depositSprites, di);
      this.placeBlob(
        sprite,
        d.nx * this.bounds.width,
        d.ny * this.bounds.height,
        d.nr * this.bounds.width * pad,
        0xff0000,
      );
      di++;
    }
    this.hideFrom(this.depositSprites, di);
  }

  private acquire(pool: Sprite[], index: number): Sprite {
    if (!pool[index]) {
      const sprite = new Sprite(this.blobTexture);
      sprite.anchor.set(0.5);
      sprite.blendMode = "add";
      this.fieldLayer.addChild(sprite);
      pool[index] = sprite;
    }
    pool[index].visible = true;
    return pool[index];
  }

  private hideFrom(pool: Sprite[], start: number): void {
    for (let i = start; i < pool.length; i++) {
      pool[i].visible = false;
    }
  }

  private placeBlob(sprite: Sprite, x: number, y: number, radius: number, tint = 0xffffff): void {
    sprite.position.set(x, y);
    const size = Math.max(2, radius * 2);
    sprite.width = size;
    sprite.height = size;
    sprite.tint = tint;
  }

  private updateForceMap(field: ForceField): void {
    field.writeToImageData(this.forceImage, this.config.field.maxStored);
    this.forceCtx.putImageData(this.forceImage, 0, 0);
    this.forceTexture.source.update();
  }

  private drawDebug(field: ForceField, divider: Divider, time: number): void {
    const g = this.debug;
    g.clear();

    const w = Math.max(1, Math.floor(this.bounds.width));
    const h = Math.max(1, Math.floor(this.bounds.height));
    const cols = field.cols;
    const rows = field.rows;
    const cellW = w / Math.max(1, cols - 1);
    const cellH = h / Math.max(1, rows - 1);
    const maxLen = Math.min(cellW, cellH) * 0.82;
    const ref = Math.max(
      this.config.field.maxStored,
      this.config.wind.strength * 12,
      this.config.ocean.strength * 12,
      1e-7,
    );

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const x = col * cellW;
        const y = row * cellH;
        const v = field.sample(x, y, time, divider);
        const mag = Math.hypot(v.x, v.y);
        const t = clamp(mag / ref, 0, 1);
        const color = forceColorHex(t);
        const alpha = 0.55 + t * 0.45;

        g.circle(x, y, 2);
        g.fill({ color, alpha });

        if (mag > 1e-8) {
          const len = 6 + t * maxLen;
          const dx = (v.x / mag) * len;
          const dy = (v.y / mag) * len;
          const ex = x + dx;
          const ey = y + dy;
          const ang = Math.atan2(dy, dx);
          const ah = 5 + t * 3;
          g.moveTo(x, y);
          g.lineTo(ex, ey);
          g.moveTo(ex, ey);
          g.lineTo(ex - Math.cos(ang - 0.45) * ah, ey - Math.sin(ang - 0.45) * ah);
          g.moveTo(ex, ey);
          g.lineTo(ex - Math.cos(ang + 0.45) * ah, ey - Math.sin(ang + 0.45) * ah);
          g.stroke({ width: 1 + t, color, alpha });
        }
      }
    }

    g.moveTo(divider.points[0]?.displayX ?? 0, divider.points[0]?.displayY ?? 0);
    for (const p of divider.points) g.lineTo(p.displayX, p.displayY);
    if (divider.isOval && divider.points.length > 2) {
      g.lineTo(divider.points[0].displayX, divider.points[0].displayY);
    }
    g.stroke({ width: 1, color: 0xff88ee, alpha: 0.5 });

    this.drawForceLabels(field, divider, time, w, h, cellW, cellH);
  }

  private drawForceLabels(
    field: ForceField,
    divider: Divider,
    time: number,
    w: number,
    h: number,
    cellW: number,
    cellH: number,
  ): void {
    if (this.debugCanvas.width !== w || this.debugCanvas.height !== h) {
      this.debugCanvas.width = w;
      this.debugCanvas.height = h;
      this.debugTexture.destroy(false);
      this.debugTexture = Texture.from(this.debugCanvas);
      this.debugTexture.source.scaleMode = "nearest";
      this.debugVectors.texture = this.debugTexture;
    }

    const ctx = this.debugCtx;
    ctx.clearRect(0, 0, w, h);
    ctx.font = "9px ui-monospace, SFMono-Regular, Menlo, monospace";
    ctx.textBaseline = "bottom";

    const ref = Math.max(
      this.config.field.maxStored,
      this.config.wind.strength * 12,
      this.config.ocean.strength * 12,
      1e-7,
    );

    for (let row = 0; row < field.rows; row++) {
      for (let col = 0; col < field.cols; col++) {
        const x = col * cellW;
        const y = row * cellH;
        const v = field.sample(x, y, time, divider);
        const mag = Math.hypot(v.x, v.y);
        const t = clamp(mag / ref, 0, 1);
        ctx.fillStyle = forceColor(t);
        ctx.fillText(formatForce(mag), x + 5, y - 3);
      }
    }

    this.debugTexture.source.update();
    this.debugVectors.texture = this.debugTexture;
    this.debugVectors.position.set(0, 0);
    this.debugVectors.width = w;
    this.debugVectors.height = h;
    this.debugVectors.visible = true;
    this.debugVectors.alpha = 1;
  }
}

function formatForce(mag: number): string {
  if (mag < 1e-8) return "0";
  return mag.toExponential(1);
}

function forceColorHex(t: number): number {
  const r = Math.round(68 + t * 187);
  const g = Math.round(102 + t * 80);
  const b = Math.round(136 - t * 80);
  return (r << 16) | (g << 8) | b;
}

function forceColor(t: number): string {
  const r = Math.round(68 + t * 187);
  const g = Math.round(102 + t * 80);
  const b = Math.round(136 - t * 80);
  const a = 0.55 + t * 0.45;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function createSoftCircleTexture(size: number): Texture {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const img = ctx.createImageData(size, size);
  const cx = (size - 1) * 0.5;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (x - cx) / cx;
      const dy = (y - cx) / cx;
      const d2 = dx * dx + dy * dy;
      const v = d2 >= 1 ? 0 : (1 - d2) * (1 - d2);
      const b = Math.round(v * 255);
      const i = (y * size + x) * 4;
      img.data[i] = b;
      img.data[i + 1] = b;
      img.data[i + 2] = b;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const texture = Texture.from(canvas);
  texture.source.scaleMode = "linear";
  return texture;
}
